import { useState, useRef, useEffect, useCallback } from 'react';
import * as faceapi from '@vladmandic/face-api';
import JSZip from 'jszip';
import { ref, getBlob } from 'firebase/storage';
import { storage } from '../../firebase';

const MODEL_URL = '/models/face-api';
const DETECTOR_OPTIONS = new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.2 });
const SSD_OPTIONS = new faceapi.SsdMobilenetv1Options({ minConfidence: 0.3 });

const getStoragePathFromUrl = (url) => {
  try {
    const u = new URL(url);
    const match = u.pathname.match(/\/o\/(.+)/);
    if (match) return decodeURIComponent(match[1]);
  } catch { }
  return null;
};

const loadGalleryImage = async (url) => {
  const path = getStoragePathFromUrl(url);
  if (path) {
    try {
      const blob = await getBlob(ref(storage, path));
      const objectUrl = URL.createObjectURL(blob);
      return { img: await createImage(objectUrl), objectUrl };
    } catch { }
  }
  const img = await createImage(url);
  return { img, objectUrl: null };
};

const createImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

function FaceRecognition({ images, onClose, eventTitle }) {
  const [stage, setStage] = useState('initial');
  const [modelLoaded, setModelLoaded] = useState(false);
  const [modelError, setModelError] = useState(null);
  const [webcamActive, setWebcamActive] = useState(false);
  const [userPhoto, setUserPhoto] = useState(null);
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);
  const [noFaceInUserPhoto, setNoFaceInUserPhoto] = useState(false);
  const [noFacesInGallery, setNoFacesInGallery] = useState(false);
  const [corsError, setCorsError] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  const matches = results.filter(r => r.faceCount > 0 && r.confidence >= 55);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  const handleViewerPrev = useCallback(() => {
    setViewerIndex(i => (i === 0 ? matches.length - 1 : i - 1));
    setZoomLevel(1);
  }, [matches.length]);

  const handleViewerNext = useCallback(() => {
    setViewerIndex(i => (i === matches.length - 1 ? 0 : i + 1));
    setZoomLevel(1);
  }, [matches.length]);

  const handleViewerDownload = () => {
    const link = document.createElement('a');
    link.href = matches[viewerIndex].image.url;
    link.download = `fast-match-${Date.now()}.jpg`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadAll = async () => {
    const zip = new JSZip();
    const folder = zip.folder('matches');
    for (let i = 0; i < matches.length; i++) {
      try {
        const res = await fetch(matches[i].image.url);
        const blob = await res.blob();
        folder.file(`match-${i + 1}.jpg`, blob);
      } catch (e) {
        console.warn(`Failed to download match ${i + 1}:`, e);
      }
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `fast-matches-${Date.now()}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const stopWebcam = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setWebcamActive(false);
  }, []);

  useEffect(() => {
    const loadModels = async () => {
      try {
        const models = [
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.ssdMobilenetv1 && faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
        ].filter(Boolean);
        await Promise.all(models);
        setModelLoaded(true);
      } catch (err) {
        console.error('Face model load error:', err);
        setModelError('Failed to load face recognition models. Check console for details.');
      }
    };
    loadModels();
    return () => stopWebcam();
  }, [stopWebcam]);

  useEffect(() => {
    if (!webcamActive && videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [webcamActive]);

  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 640, height: 480 } });
      streamRef.current = stream;
      setWebcamActive(true);
      setStage('capture');
      setError(null);
      setNoFaceInUserPhoto(false);
    } catch {
      setError('Could not access camera. Please upload a photo instead.');
    }
  };

  useEffect(() => {
    if (!webcamActive || !videoRef.current) return;
    videoRef.current.srcObject = streamRef.current;
    videoRef.current.play();
  }, [webcamActive]);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const maxDim = 640;
    const scale = Math.min(maxDim / video.videoWidth, maxDim / video.videoHeight, 1);
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setUserPhoto(dataUrl);
    stopWebcam();
    runRecognition(dataUrl);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setUserPhoto(dataUrl);
      runRecognition(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const loadImage = async (src) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = (e) => { console.error('loadImage error:', e); reject(e); };
      img.src = src;
    });
    return img;
  };

  const detectFacesInImage = async (img) => {
    const detections = await faceapi
      .detectAllFaces(img, DETECTOR_OPTIONS)
      .withFaceLandmarks()
      .withFaceDescriptors();

    if (detections.length > 0) return detections;

    const ssd = await faceapi
      .detectAllFaces(img, SSD_OPTIONS)
      .withFaceLandmarks()
      .withFaceDescriptors();

    return ssd.length > 0 ? ssd : detections;
  };

  const detectSingleFaceInImage = async (img) => {
    const det = await faceapi
      .detectSingleFace(img, DETECTOR_OPTIONS)
      .withFaceLandmarks()
      .withFaceDescriptor();

    if (det) return det;

    return faceapi
      .detectSingleFace(img, SSD_OPTIONS)
      .withFaceLandmarks()
      .withFaceDescriptor();
  };

  const processImage = async (imageData, index, userDescriptor) => {
    const { img: galleryImg, objectUrl } = await loadGalleryImage(imageData.url);
    const detections = await detectFacesInImage(galleryImg);

    if (objectUrl) URL.revokeObjectURL(objectUrl);

    let bestDistance = Infinity;
    for (const det of detections) {
      const distance = faceapi.euclideanDistance(userDescriptor, det.descriptor);
      if (distance < bestDistance) bestDistance = distance;
    }

    const confidence = detections.length > 0
      ? Math.max(0, Math.min(100, Math.round((1 - bestDistance / 1.5) * 100)))
      : 0;

    return {
      image: imageData,
      index,
      distance: detections.length > 0 ? bestDistance : null,
      confidence,
      faceCount: detections.length,
    };
  };

  const runRecognition = async (photoDataUrl) => {
    if (!modelLoaded) return;
    setStage('processing');
    setError(null);
    setNoFaceInUserPhoto(false);
    setNoFacesInGallery(false);
    setCorsError(false);

    try {
      const { img: userImg } = await loadGalleryImage(photoDataUrl);
      console.log('User image loaded, detecting face...');
      const userDetection = await detectSingleFaceInImage(userImg);

      console.log('User detection result:', userDetection ? 'face found' : 'no face');
      if (!userDetection) {
        setNoFaceInUserPhoto(true);
        setStage('initial');
        return;
      }
      console.log('User descriptor length:', userDetection.descriptor.length);

      const totalImages = images.length;
      setProgress({ current: 0, total: totalImages });
      const matchResults = [];
      let totalFacesFound = 0;
      let completed = 0;
      const CONCURRENCY = 8;

      for (let start = 0; start < totalImages; start += CONCURRENCY) {
        const batch = images.slice(start, start + CONCURRENCY);
        const batchResults = await Promise.allSettled(
          batch.map((img, i) =>
            processImage(img, start + i, userDetection.descriptor)
          )
        );

        for (const [batchIdx, result] of batchResults.entries()) {
          if (result.status === 'fulfilled') {
            totalFacesFound += result.value.faceCount;
            matchResults.push(result.value);
          } else {
            const globalIdx = start + batchIdx;
            console.warn(`Image ${globalIdx} processing error:`, result.reason);
            const msg = result.reason?.message || result.reason?.toString() || '';
            if (/tainted|cross.?origin|cors|insecure/i.test(msg)) {
              setCorsError(true);
            }
            matchResults.push({
              image: images[globalIdx],
              index: globalIdx,
              distance: null,
              confidence: 0,
              faceCount: 0,
              error: true,
            });
          }
        }

        completed += batch.length;
        setProgress({ current: completed, total: totalImages });
      }

      console.log(`Total faces detected across all images: ${totalFacesFound}`);

      if (totalFacesFound === 0) {
        setNoFacesInGallery(true);
      }

      matchResults.sort((a, b) => (b.confidence - a.confidence) || (b.faceCount - a.faceCount));
      setResults(matchResults);
      setStage('results');
    } catch (err) {
      console.error('Recognition error:', err);
      setError('An error occurred during face recognition.');
    }
  };

  const reset = () => {
    stopWebcam();
    setStage('initial');
    setUserPhoto(null);
    setResults([]);
    setProgress({ current: 0, total: 0 });
    setError(null);
    setNoFaceInUserPhoto(false);
    setNoFacesInGallery(false);
    setCorsError(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    if (viewerIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setViewerIndex(null);
      if (e.key === 'ArrowLeft') handleViewerPrev();
      if (e.key === 'ArrowRight') handleViewerNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [viewerIndex, handleViewerNext, handleViewerPrev]);

  const stepIndex = stage === 'results' ? 2 : stage === 'processing' ? 1 : 0;
  const steps = ['Photo', 'Scanning', 'Results'];

  const processingStatus = () => {
    if (!progress.total) return 'Detecting faces';
    const pct = progress.current / progress.total;
    if (pct < 0.25) return 'Detecting faces';
    if (pct < 0.6)  return 'Comparing features';
    if (pct < 0.9)  return 'Analyzing matches';
    return 'Finishing up';
  };

  return (
    <>
      {viewerIndex !== null && matches[viewerIndex] && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-[60] flex items-center justify-center"
          onClick={() => setViewerIndex(null)}
        >
          <div className="relative max-w-4xl w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-2 text-white">
              <span className="text-lg">{viewerIndex + 1} / {matches.length}</span>
              <div className="flex gap-3 items-center">
                <button onClick={handleViewerDownload} className="hover:text-gray-300 text-sm flex items-center gap-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
                <div className="flex items-center gap-1">
                  <button onClick={() => setZoomLevel(z => Math.max(1, z - 0.5))} className="hover:text-gray-300 text-sm px-2">−</button>
                  <span className="text-sm">{Math.round(zoomLevel * 100)}%</span>
                  <button onClick={() => setZoomLevel(z => Math.min(3, z + 0.5))} className="hover:text-gray-300 text-sm px-2">+</button>
                </div>
                <button onClick={() => setViewerIndex(null)} className="text-white text-3xl hover:text-gray-300 ml-2">&times;</button>
              </div>
            </div>

            {matches.length > 1 && (
              <button onClick={handleViewerPrev} className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-12 text-white text-5xl hover:text-gray-300">
                &#8249;
              </button>
            )}

            <div className="overflow-auto max-h-[80vh] flex items-center justify-center">
              <img
                src={matches[viewerIndex].image.url}
                className="max-w-full max-h-[80vh] object-contain rounded-lg transition-transform"
                style={{ transform: `scale(${zoomLevel})`, cursor: zoomLevel < 3 ? 'zoom-in' : 'zoom-out' }}
                alt={`Match ${viewerIndex + 1}`}
                onClick={() => {
                  if (zoomLevel < 3) setZoomLevel(z => Math.min(3, z + 0.5));
                  else setZoomLevel(1);
                }}
              />
            </div>

            {matches.length > 1 && (
              <button onClick={handleViewerNext} className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-12 text-white text-5xl hover:text-gray-300">
                &#8250;
              </button>
            )}
          </div>
        </div>
      )}

      <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-mainBlue">Find Me in Photos</h2>
          <button
            onClick={onClose}
            className="text-2xl text-gray-500 hover:text-gray-700"
          >
            &times;
          </button>
        </div>

        {/* Step indicator */}
        {modelLoaded && !modelError && (
          <div className="flex items-center justify-center gap-0 px-6 pt-4 pb-0">
            {steps.map((label, i) => (
              <div key={i} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                      i < stepIndex
                        ? 'bg-mainBlue text-white'
                        : i === stepIndex
                        ? 'bg-mainBlue text-white ring-4 ring-mainBlue/20'
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {i < stepIndex ? (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </div>
                  <span className={`text-xs mt-1 ${i === stepIndex ? 'text-mainBlue font-medium' : 'text-gray-400'}`}>
                    {label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-16 sm:w-24 h-px mb-4 mx-1 transition-all duration-500 ${i < stepIndex ? 'bg-mainBlue' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
        )}

        <div className="p-4">
          {!modelLoaded && !modelError && (
            <div className="text-center py-10">
              <div className="relative w-12 h-12 mx-auto mb-4">
                <div className="absolute inset-0 rounded-full border-4 border-mainBlue/20" />
                <div className="absolute inset-0 rounded-full border-4 border-mainBlue border-t-transparent animate-spin" />
              </div>
              <p className="text-gray-700 font-medium">Loading AI models</p>
            </div>
          )}

          {modelError && (
            <div className="text-center py-8">
              <p className="text-red-500 mb-2">{modelError}</p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-mainBlue text-white rounded hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          )}

          {modelLoaded && stage === 'initial' && (
            <div className="text-center py-8 px-4">
              {/* Face scan icon */}
              <div className="w-16 h-16 mx-auto mb-5 relative">
                <svg className="w-full h-full text-mainBlue" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 3H5a2 2 0 00-2 2v4" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 3h4a2 2 0 012 2v4" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 21H5a2 2 0 01-2-2v-4" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 21h4a2 2 0 002-2v-4" />
                  <circle cx="12" cy="11" r="3" strokeLinecap="round" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.5 16.5c.83-1.19 2.03-2 3.5-2s2.67.81 3.5 2" />
                </svg>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mb-1">Find yourself in the gallery</h3>
              <p className="text-gray-500 text-sm mb-6 max-w-xs mx-auto">
                Use a selfie or uploaded photo to scan {images.length} event photo{images.length !== 1 ? 's' : ''} for your face.
              </p>

              {error && <p className="text-red-500 mb-4 text-sm">{error}</p>}
              {noFaceInUserPhoto && (
                <p className="text-red-500 mb-4 text-sm bg-red-50 px-4 py-2 rounded-lg inline-block">
                  No face detected: try a clearer, front-facing photo.
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={startWebcam}
                  className="px-6 py-3 bg-mainBlue text-white rounded-lg hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-2 font-medium shadow-sm"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Take Selfie
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-6 py-3 bg-white text-mainBlue border-2 border-mainBlue rounded-lg hover:bg-gray-50 active:scale-95 transition-all flex items-center justify-center gap-2 font-medium"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Upload Photo
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
          )}

          {modelLoaded && stage === 'capture' && (
            <div className="text-center py-4 px-4">
              <div className="relative max-w-md mx-auto bg-gray-900 rounded-xl overflow-hidden shadow-lg">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full"
                />
                {/* Face guide overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div
                    className="border-2 border-white/70 rounded-[50%]"
                    style={{
                      width: '52%',
                      height: '72%',
                      boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
                    }}
                  />
                </div>
                {/* Corner brackets */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="relative" style={{ width: '52%', height: '72%' }}>
                    <span className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-white rounded-tl-sm" />
                    <span className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-white rounded-tr-sm" />
                    <span className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-white rounded-bl-sm" />
                    <span className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-white rounded-br-sm" />
                  </div>
                </div>
                <canvas ref={canvasRef} className="hidden" />
              </div>
              <p className="text-gray-500 text-sm mt-3 mb-4">Align your face within the oval</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={capturePhoto}
                  className="px-6 py-3 bg-mainBlue text-white rounded-lg hover:opacity-90 active:scale-95 transition-all flex items-center gap-2 font-medium shadow-sm"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  </svg>
                  Capture Photo
                </button>
                <button
                  onClick={reset}
                  className="px-6 py-3 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 active:scale-95 transition-all font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {modelLoaded && stage === 'processing' && (
            <div className="text-center py-8 px-4">
              {userPhoto && (
                <div className="relative w-24 h-24 mx-auto mb-6">
                  <img
                    src={userPhoto}
                    alt="Your face"
                    className="w-full h-full object-cover rounded-full"
                  />
                  {/* Spinning arc */}
                  <div className="absolute -inset-1.5 rounded-full border-[3px] border-mainBlue/15 border-t-mainBlue animate-spin pointer-events-none" />
                </div>
              )}

              <p className="text-gray-700 font-medium mb-1">{processingStatus()}</p>
              <p className="text-gray-400 text-sm mb-6">
                Scanning {progress.total || images.length} photo{(progress.total || images.length) !== 1 ? 's' : ''} for your face
              </p>

              {progress.total > 0 && (
                <div className="max-w-sm mx-auto">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-gray-400">{progress.current} of {progress.total}</span>
                    <span className="font-semibold text-mainBlue">
                      {Math.round((progress.current / progress.total) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-mainBlue h-full rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${Math.max(2, (progress.current / progress.total) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {modelLoaded && stage === 'results' && (
            <div className="py-4">
              {error && (
                <div className="text-center py-4">
                  <p className="text-red-500 mb-2">{error}</p>
                </div>
              )}

              {noFacesInGallery && !corsError && (
                <div className="text-center py-4">
                  <p className="text-gray-600 mb-2">
                    No recognizable faces were found in any of the gallery images.
                  </p>
                  <p className="text-gray-500 text-sm mb-4">
                    This could mean the images are too small, blurry, or the faces are at extreme angles.
                  </p>
                  <button
                    onClick={reset}
                    className="px-6 py-3 bg-mainBlue text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {noFacesInGallery && corsError && (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <p className="text-gray-700 font-medium mb-1">Gallery images blocked by browser security</p>
                  <p className="text-gray-500 text-sm mb-1 max-w-md mx-auto">
                    The gallery images are stored on Firebase Storage but CORS is not configured for this domain.
                    This prevents the face recognition AI from accessing the image data.
                  </p>
                  <p className="text-gray-400 text-xs mb-4">
                    Contact the site admin to run: <code className="bg-gray-100 px-1 rounded">gsutil cors set cors.json gs://&lt;bucket&gt;</code>
                  </p>
                  <button
                    onClick={reset}
                    className="px-6 py-3 bg-mainBlue text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {matches.length > 0 && (
                <>
                  <div className="text-center mb-5">
                    <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-gray-700 font-semibold">
                      Found <span className="text-mainBlue">{matches.length}</span> photo{matches.length !== 1 ? 's' : ''} with you
                    </p>
                    <p className="text-gray-400 text-sm mt-0.5">in {eventTitle || 'this event'}</p>
                  </div>
                  <ul className="md:columns-3 columns-2 gap-1 md:gap-0 mx-2 md:mx-5">
                    {matches.map((result, i) => (
                      <li
                        key={result.index}
                        className="m-0.5 md:m-1 hover:opacity-70 cursor-pointer"
                        onClick={() => { setViewerIndex(i); setZoomLevel(1); }}
                      >
                        <img
                          src={result.image.url}
                          alt={`Match ${i + 1}`}
                          className="w-full h-auto rounded"
                          loading="lazy"
                        />
                      </li>
                    ))}
                  </ul>
                  <div className="text-center mt-6 flex gap-3 justify-center">
                    <button
                      onClick={handleDownloadAll}
                      className="px-6 py-3 bg-white text-mainBlue border-2 border-mainBlue rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download All
                    </button>
                    <button
                      onClick={reset}
                      className="px-6 py-3 bg-mainBlue text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      Try Another Photo
                    </button>
                  </div>
                </>
              )}

              {matches.length === 0 && !noFacesInGallery && !error && (
                <div className="text-center py-8 px-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-700 font-medium mb-1">No matches found</p>
                  <p className="text-gray-400 text-sm mb-5">
                    Try a clearer, well-lit front-facing photo for better results.
                  </p>
                  <button
                    onClick={reset}
                    className="px-6 py-3 bg-mainBlue text-white rounded-lg hover:opacity-90 active:scale-95 transition-all font-medium"
                  >
                    Try Another Photo
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default FaceRecognition;
